import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from 'environments/environment';

const ID_TOKEN_KEY = 'GLSID';
const TOKEN_MAX_AGE = 60 * 60 * 24 * 1; // one day

@Injectable({
  providedIn: 'root'
})
export class JwtService {
  private sessionToken: string;
  private tokenSubject: BehaviorSubject<string>;

  constructor() {
    this.sessionToken = this.loadSessionToken();
    this.tokenSubject = new BehaviorSubject(this.sessionToken);
    // eslint-disable-next-line
    window.onfocus = () => this.checkSessionToken(this.loadSessionToken());
  }

  public getToken$(): Observable<string> {
    return this.tokenSubject.asObservable();
  }

  getSessionToken() {
    const token = this.loadSessionToken();
    if (token) {
      this.checkSessionToken(token);
      return token;
    }
    return null;
  }

  getApiKey() {
    return sessionStorage.getItem(ID_TOKEN_KEY);
  }

  saveSessionToken(token) {
    this.sessionToken = token;
    this.tokenSubject.next(token);
    // eslint-disable-next-line
    document.cookie = `${ID_TOKEN_KEY}=${token}; max-age=${TOKEN_MAX_AGE}; domain=${environment.DOMAIN}; path=/; SameSite=None; Secure`;
  }

  saveApiKey(token) {
    sessionStorage.setItem(ID_TOKEN_KEY, token);
  }

  destroyApiKey() {
    sessionStorage.removeItem(ID_TOKEN_KEY);
  }

  destroySessionToken() {
    this.sessionToken = null;
    this.tokenSubject.next(null);
    // eslint-disable-next-line
    document.cookie = `${ID_TOKEN_KEY}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=${environment.DOMAIN}; path=/; SameSite=None; Secure`;
  }

  destroyTokens() {
    this.destroySessionToken();
    this.destroyApiKey();
  }

  loadSessionToken() {
    // eslint-disable-next-line
    const cookie = document.cookie.split(';').find((item) => item.trim().startsWith(ID_TOKEN_KEY));
    if (cookie) {
      const token = cookie.split('=')[1];
      return token;
    }
    return null;
  }

  checkSessionToken(token) {
    if (this.sessionToken !== token) {
      this.destroyApiKey();
      location.reload();
    }
  }

  isExpired(token) {
    const { exp = 0 } = this.getTokenData(token);
    const expirationDate = new Date(exp * 1000);
    return expirationDate.getTime() < Date.now();
  }

  getTokenData(token) {
    if (!token) return {};
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      // eslint-disable-next-line
      return JSON.parse(jsonPayload);
    } catch (error) {
      return {};
    }
  }
}
